import { cn } from "@/lib/utils";
import { LucideIcon, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui";
import * as React from "react";

export interface TabButtonProps {
  label: string;
  selected: boolean;
  icon: LucideIcon;
  onClick: () => void;
}

export function TabButton({ label, selected, icon: IconComponent, onClick }: TabButtonProps) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      className={cn(
        "text-sm font-semibold flex items-center gap-1.5 px-3 h-8 transition-all rounded-lg",
        selected
          ? "bg-indigo-50 text-primary hover:bg-indigo-100"
          : "text-slate-500 hover:text-primary hover:bg-indigo-50/50"
      )}
    >
      <IconComponent
        className={cn("size-3.5", selected ? "text-primary" : "text-slate-400 group-hover:text-primary")}
      />
      {label}
    </Button>
  );
}

export interface ReputationTab {
  key: string;
  title: string;
  icon: LucideIcon;
}

export interface ReputationHeaderProps {
  selectedTabKey: string;
  onTabChange: (tabKey: string) => void;
  mainTabKey?: string;
  mainTabTitle: string;
  mainTabIcon: LucideIcon;
  backToMainTitle?: string;
  tabs: ReputationTab[];
  rightActions?: React.ReactNode;
  /** A page inside a tab (Form Routes inside Forms): a back link and the page's name replace the tabs. */
  subPage?: { title: string; icon: LucideIcon; backTitle: string; onBack: () => void };
}

export function ReputationHeader({
  selectedTabKey,
  onTabChange,
  mainTabKey,
  mainTabTitle,
  mainTabIcon: MainIcon,
  backToMainTitle,
  tabs,
  rightActions,
  subPage,
}: ReputationHeaderProps) {
  const showBackLink = backToMainTitle && selectedTabKey !== mainTabKey;

  return (
    <div className="w-full flex flex-row flex-wrap gap-4 justify-between items-center px-4 pt-4 pb-2 shrink-0 border-b border-slate-100">
      <div className="flex flex-row gap-4 items-center flex-wrap">
        {showBackLink ? (
          <div
            className="group flex flex-row items-center gap-1.5 cursor-pointer hover:bg-slate-50 py-1.5 px-2 -ml-2 rounded-lg transition-colors select-none"
            onClick={() => onTabChange(mainTabKey!)}
          >
            <ChevronLeft className="size-4 text-slate-400 group-hover:text-primary transition-colors" />
            <div className="text-base font-bold text-slate-600 group-hover:text-primary transition-colors">
              {backToMainTitle}
            </div>
          </div>
        ) : (
          <div className="flex flex-row items-center gap-2">
            <div className="bg-indigo-50/50 text-primary p-1.5 rounded-lg">
              <MainIcon className="size-4" />
            </div>
            <div className="text-xl font-bold text-slate-800 tracking-tight">{mainTabTitle}</div>
          </div>
        )}

        {subPage ? (
          <div className="flex flex-row items-center gap-1.5 pl-4 ml-2 border-l-2 border-slate-100">
            <Button
              variant="ghost"
              size="sm"
              onClick={subPage.onBack}
              className="text-sm font-semibold flex items-center gap-1.5 px-3 h-8 rounded-lg text-slate-500 hover:text-primary hover:bg-indigo-50/50"
            >
              <ChevronLeft className="size-3.5 text-slate-400" />
              {subPage.backTitle}
            </Button>
            {/* Styled as the selected tab, since this page is where the admin is */}
            <h1
              aria-current="page"
              className="text-sm font-semibold flex items-center gap-1.5 px-3 h-8 rounded-lg bg-indigo-50 text-primary select-none"
            >
              <subPage.icon className="size-3.5 text-primary" />
              {subPage.title}
            </h1>
          </div>
        ) : (
          <div className="flex flex-row items-center gap-1.5 pl-4 ml-2 border-l-2 border-slate-100">
            {tabs.map(tab => (
              <TabButton
                key={`tab-btn-${tab.key}`}
                label={tab.title}
                icon={tab.icon}
                selected={selectedTabKey === tab.key}
                onClick={() => onTabChange(tab.key)}
              />
            ))}
          </div>
        )}
      </div>

      {rightActions && <div className="flex flex-row gap-2 items-center">{rightActions}</div>}
    </div>
  );
}
